/**
 * Section-placement helpers — pure logic with no UI dependencies.
 *
 * Extracted here so it can be unit-tested without loading any React or DOM
 * machinery.  The three quote pages (quote-ai, quote-manual, quote-template)
 * all import from this module.
 */
import { getItemSubGroup, getWorkItemGroupingOverride, SUB_GROUP_SECTION } from "@workspace/api-zod/work-type";

/** Full visual order for quote-only main-section placement across every output. */
export const QUOTE_PLACEMENT_STAGE_ORDER = [
  "Preparation",
  "Demolition",
  "Substructure",
  "Structure",
  "Roofing",
  "External Works",
  "Finishes",
  "Services",
  "Other",
] as const;

export const CATEGORY_TO_STAGE: Record<string, string> = {
  preliminaries:  "Preparation",
  scaffolding:    "Preparation",
  demolition:     "Demolition",
  waste:          "Demolition",
  groundworks:    "Substructure",
  foundations:    "Substructure",
  damp_proofing:  "Substructure",
  brickwork:      "Structure",
  steel:          "Structure",
  roofing:        "Roofing",
  flat_roofing:   "Roofing",
  flat_roof:      "Roofing",
  roof_repair:    "Roofing",
  fascias_soffits:"Roofing",
  fascias:        "Roofing",
  insulation:     "Structure",
  windows_doors:  "External Works",
  driveway:       "External Works",
  carpentry:      "Finishes",
  internal_walls: "Finishes",
  plastering:     "Finishes",
  flooring:       "Finishes",
  tiling:         "Finishes",
  decorating:     "Finishes",
  kitchens:       "Finishes",
  bathrooms:      "Finishes",
  making_good:    "Finishes",
  fire_protection:"Finishes",
  electrics:      "Services",
  plumbing:       "Services",
  heating:        "Services",
  ventilation:    "Services",
  other:          "Other",
};

/**
 * Superset used by the template flow — extends the base map.
 * `heating` and `driveway` were moved to the base map so all quote types
 * (AI, manual, template, core pipeline) resolve them correctly.
 */
export const CATEGORY_TO_STAGE_T: Record<string, string> = {
  ...CATEGORY_TO_STAGE,
};

/**
 * Returns the display section name for a line item used in the AI and manual
 * quote pages.
 *
 * Special overrides before the map lookup:
 *  - carpentry + roof_carpentry trade → "Structure" (structural timber, not
 *    finish carpentry)
 *  - damp_proofing + name containing "tanking" / "waterproofing" → "Finishes"
 *    (bathroom tanking membranes belong with finishes, not substructure)
 */
export function getItemStage(category: string, trade?: string, name?: string): string {
  const titleGrouping = getWorkItemGroupingOverride(category, name);
  if (titleGrouping) return titleGrouping.stage;
  if (category === "carpentry" && trade === "roof_carpentry") return "Structure";
  if (category === "damp_proofing" && name && /tanking|waterproofing/i.test(name)) return "Finishes";
  // A services subgroup must not be split across Finishes and Services merely
  // because AI used bathrooms/tiling for some of its fixture installations.
  const subGroup = getItemSubGroup(category, name);
  if (subGroup && SUB_GROUP_SECTION[subGroup] === "Services") return "Services";
  // Roofing category variants not in the map (e.g. "roof_structure") — but
  // structural roof timbers (roof_carpentry, handled above) stay in Structure.
  if (category.startsWith("roof")) return "Roofing";
  // Sanitary plumbing items are sometimes categorised as "bathrooms" → Finishes,
  // but they are services work and belong in the Services stage.
  if (category === "bathrooms" && name && /sanitar(y|ware|iation)|\bwc\s+sanit/i.test(name)) return "Services";
  // Heating items can be miscategorised as a Finishes trade by the AI; rescue
  // them by name when the category-map would otherwise place them in Finishes.
  if (name && /\bheating\b/i.test(name) && CATEGORY_TO_STAGE[category] === "Finishes") return "Services";
  return CATEGORY_TO_STAGE[category] || "Other";
}

/**
 * Template-page variant — same overrides, uses the extended CATEGORY_TO_STAGE_T
 * map.  The `trade` parameter is non-optional here because template items always
 * carry a trade value.
 */
export function getItemStageT(category: string, trade: string, name?: string): string {
  const titleGrouping = getWorkItemGroupingOverride(category, name);
  if (titleGrouping) return titleGrouping.stage;
  if (category === "carpentry" && trade === "roof_carpentry") return "Structure";
  if (category === "damp_proofing" && name && /tanking|waterproofing/i.test(name)) return "Finishes";
  const subGroup = getItemSubGroup(category, name);
  if (subGroup && SUB_GROUP_SECTION[subGroup] === "Services") return "Services";
  if (category.startsWith("roof")) return "Roofing";
  if (category === "bathrooms" && name && /sanitar(y|ware|iation)|\bwc\s+sanit/i.test(name)) return "Services";
  if (name && /\bheating\b/i.test(name) && CATEGORY_TO_STAGE_T[category] === "Finishes") return "Services";
  return CATEGORY_TO_STAGE_T[category] || "Other";
}

/**
 * Quote-local placement.  These values deliberately live on the quote line
 * item rather than in the cost library: a builder may move one quoted item
 * without changing how that library item is classified for future quotes.
 */
export interface PlacementItem {
  category?: string | null;
  name?: string | null;
  trade?: string | null;
  displayStage?: string | null;
  displaySubGroup?: string | null;
}

export function getEffectiveStage(item: PlacementItem): string {
  const override = item.displayStage?.trim();
  if (override) return override;
  // Summary surfaces use the same category/name classifier as the quote
  // editor.  Keep this fallback here (rather than copying it into exporters)
  // so all consumers agree.
  return getItemStage(item.category || "", item.trade || undefined, item.name || undefined);
}

export function getEffectiveSubGroup(item: PlacementItem): string | null {
  // null/undefined means "derive from classification"; an explicit blank
  // means the item was moved to the main stage rather than a named subsection.
  if (item.displaySubGroup !== null && item.displaySubGroup !== undefined) {
    return item.displaySubGroup.trim() || null;
  }
  return getItemSubGroup(item.category, item.name);
}
